<template>
  <div class="dd-sub-card dd-sub-card--column" v-if="subscription">
    <div class="dd-flag">{{ level.price }} {{ level.currency }}/{{ $t('periods.' + level.paymentPeriod) }}</div>
    <h3 class="dd-sub-card__title">{{ $t('wall.subscription.your_subscription') }}</h3>
    <div class="dd-sub-card__row">
      <div class="dd-sub-card__level">
        <div class="dd-sub-card__level-name">{{ level.name }}</div>
        <div v-if="paidTillFormatted" class="dd-sub-card__meta">{{ $t('subscriptions.paid_till') }} {{ paidTillFormatted }}</div>
        <div v-if="expiringSoon" class="dd-sub-card__meta dd-sub-card__meta--warn">
          {{ $t('wall.subscription.expires_soon', { days: daysLeft }) }}
        </div>
      </div>
    </div>
    <div v-if="nextLevel" class="dd-sub-card__next">{{ $t('wall.subscription.scheduled_upgrade', {name: nextLevel.name}) }}</div>
    <div class="dd-sub-card__footer dd-sub-card__footer--bottom">
      <div class="dd-sub-card__settings dd-name-field">
        <label class="dd-label" :for="idPublicName">{{ $t('wall.subscription.public_name_label') }}</label>
        <div class="input-suffix">
          <input :id="idPublicName" type="text" class="textinput textinput--sm form-control" maxlength="50"
                 :placeholder="$t('wall.subscription.public_name_placeholder')"
                 v-model="publicNameLocal" />
          <button class="btn btn--icon btn--icon-abs" :disabled="saving" @click="onSavePublicName" :aria-label="$t('common.save')" :title="$t('common.save')">
            <img :src="saveIcon" alt="" />
          </button>
        </div>
        <span v-if="savedShown" class="dd-crypto__copied">{{ $t('common.saved') }}</span>
      </div>

      <div class="dd-sub-card__actions">
        <button v-if="showPay" class="btn" :disabled="busy" @click="onPay">{{ $t('wall.subscription.pay') }}</button>
        <template v-if="isCanceled">
          <div class="dd-sub-card__next dd-sub-card__next--inline">
            {{ $t('subscriptions.you_have_canceled_this_subscription') }}
            <a class="link-cta" href="#" @click.prevent="onContinue">{{ $t('subscriptions.continue_subscription') }}</a>
          </div>
        </template>
        <template v-else-if="isGiftNonRecurrent">
          <div class="dd-sub-card__next dd-sub-card__next--inline">
            {{ $t('subscriptions.paid_as_gift_non_recurrent') }}
          </div>
        </template>
        <template v-else-if="isCryptoNonRecurrent">
          <div class="dd-sub-card__next dd-sub-card__next--inline">
            {{ $t('subscriptions.paid_with_crypto_non_recurrent') }}
          </div>
        </template>
        <button v-else class="btn btn--outline" :disabled="busy" @click="onUnsubscribe">{{ $t('wall.subscription.cancel') }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import SubscriptionPaymentMethodStatus from "@/store/models/SubscriptionPaymentMethodStatus";
import SubscriptionStatus from "@/store/models/SubscriptionStatus";
import moment from 'moment';
import saveIcon from '@/assets/img/save.svg';
export default {
  name: 'CurrentSubscriptionCard',
  data() {
    return { publicNameLocal: '', saveIcon, savedShown: false, savedTimer: null };
  },
  computed: {
    subscription() { return this.$store.getters.currentSubscription; },
    level() { return this.$store.getters.currentLevel || {}; },
    nextLevel() { return this.$store.getters.nextLevel; },
    busy() { return this.$store.getters.subscriptionRequestInProgress; },
    saving() { return this.$store.state.publicNameSaveInProgress; },
    idPublicName() { return 'public-name-input'; },
    paidTillFormatted() {
      const s = this.subscription;
      if (!s || !s.paidTill) return null;
      return moment(s.paidTill).format(this.$t('date_time_format'));
    },
    isCanceled() {
      const s = this.subscription;
      if (!s) return false;
      if (this.isCryptoNonRecurrent) return false;
      if (this.isGiftNonRecurrent) return false;
      return !s.recurrent && s.nextRecurrentPayment == null && s.nextLevel == null && s.status === SubscriptionStatus.Paid;
    },
    isGiftNonRecurrent() {
      const s = this.subscription;
      if (!s || !s.isGifted) return false;
      const status = this.$store.getters.subscriptionPaymentMethodStatus;
      return (
        status === SubscriptionPaymentMethodStatus.NonRecurrent &&
        s.status === SubscriptionStatus.Paid
      );
    },
    isCryptoNonRecurrent() {
      const s = this.subscription;
      if (!s || s.isGifted) return false;
      const status = this.$store.getters.subscriptionPaymentMethodStatus;
      return (
        status === SubscriptionPaymentMethodStatus.NonRecurrent &&
        s.status === SubscriptionStatus.Paid
      );
    },
    showPay() {
      const s = this.subscription;
      if (!s) return false;
      const failed = this.$store.getters.subscriptionPaymentMethodStatus === SubscriptionPaymentMethodStatus.Failed;
      const lastRecurrentFailed = !!s.lastFailedPaymentAttempt;
      const notPaid = s.status !== SubscriptionStatus.Paid;
      return failed || lastRecurrentFailed || notPaid;
    },
    daysLeft() {
      if (!this.subscription?.paidTill) return null;
      const now = moment();
      const pt = moment(this.subscription.paidTill);
      return Math.max(pt.diff(now, 'days'), 0);
    },
    expiringSoon() {
      return this.daysLeft !== null && this.daysLeft <= 7;
    },
  },
  methods: {
    onPay() { this.$emit('renew', this.level); },
    onUnsubscribe() { this.$store.dispatch('unsubscribe'); },
    onContinue() {
      const id = this.level?.ID;
      if (!id) return;
      this.$store.dispatch('subscribe', id);
    },
    onSavePublicName() {
      this.$store.dispatch('savePublicSponsorName', this.publicNameLocal || '')
        .then(() => {
          if (this.savedTimer) { clearTimeout(this.savedTimer); this.savedTimer = null; }
          this.savedShown = true;
          this.savedTimer = setTimeout(() => { this.savedShown = false; this.savedTimer = null; }, 2000);
        });
    },
  },
  beforeDestroy() {
    if (this.savedTimer) { clearTimeout(this.savedTimer); this.savedTimer = null; }
  },
  watch: {
    subscription: {
      immediate: true,
      handler(val) {
        if (val) {
          this.$store.dispatch('fetchPublicSponsorName').then(() => {
            this.publicNameLocal = this.$store.getters.publicSponsorName || '';
          });
        }
      }
    }
  }
}
</script>

<style scoped>
.dd-sub-card.dd-sub-card--column {
  display: flex;
  flex-direction: column;
  position: relative;
}

.dd-sub-card__footer {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
}

.dd-sub-card__footer--bottom {
  margin-top: 12px;
}

.dd-sub-card__settings {
  margin-top: 12px;
  max-width: 360px;
}

.dd-name-field { position: relative; padding-bottom: 18px; }

.input-suffix { position: relative; }

.textinput--sm {
  height: 36px;
  font-size: 14px;
  /* button(36) + right gap (6) */
  padding: 6px 42px 6px 10px;
}

.btn--icon {
  width: 36px;
  height: 36px;
  padding: 0;
  display: inline-grid;
  place-items: center;
  /* Use channel accent color (control_color) for icon button */
  background: var(--dd-accent);
  color: var(--dd-accent-contrast);
  border: none;
  border-radius: 8px; /* match input radius */
}

.btn--icon-abs {
  position: absolute;
  right: 6px; /* slightly larger right offset */
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
}

.btn--icon img,
.btn--icon svg {
  width: 18px;
  height: 18px;
  pointer-events: none;
  filter: invert(1);
}

.dd-sub-card__actions--bottomright {
  margin-top: auto;
  align-self: flex-end;
  display: flex;
  gap: 8px;
}

.dd-sub-card__next--inline {
  margin: 0;
  line-height: 1.3;
}
.dd-sub-card__meta--warn {
  color: #d45a00;
}
</style>
